import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

// Lazy — keeps the ads landing page light until the visitor converts.
const RvMatchForm = lazy(() => import('./LandingPageWithForm'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Two-slot uploader: snap/select the Certification Label and the Tire &
// Loading label together, then read both in one pass. Selecting 2 photos in
// one picker fills both slots.
const DualLabelUploader = ({ onAnalyze, busy, idPrefix = 'main' }) => {
  const [certFile, setCertFile] = useState(null);
  const [tireFile, setTireFile] = useState(null);
  const certRef = useRef(null);
  const tireRef = useRef(null);

  const handleFiles = (slot, list) => {
    const files = Array.from(list || []).filter((f) => f.type?.startsWith('image/'));
    if (!files.length) return;
    if (files.length >= 2) { setCertFile(files[0]); setTireFile(files[1]); return; }
    if (slot === 'cert') setCertFile(files[0]); else setTireFile(files[0]);
  };

  const analyze = async () => {
    const ok = await onAnalyze([certFile, tireFile].filter(Boolean));
    if (ok) { setCertFile(null); setTireFile(null); }
  };

  const Slot = ({ slot, file, inputRef, title, hint }) => (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      disabled={busy}
      className={`relative w-full rounded-xl border-2 border-dashed p-3 text-left transition-colors disabled:opacity-60 ${
        file ? 'border-emerald-400 bg-emerald-50/70' : 'border-[#004E86]/30 bg-blue-50/40 hover:bg-blue-50'
      }`}
      data-testid={`rigready-${idPrefix}-${slot}-slot`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => { handleFiles(slot, e.target.files); e.target.value = ''; }}
        data-testid={`rigready-${idPrefix}-${slot}-input`}
      />
      <div className="flex items-center gap-2.5">
        {file ? (
          <img src={URL.createObjectURL(file)} alt="" className="w-10 h-10 rounded-lg object-cover border border-emerald-300" />
        ) : (
          <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-lg">📸</div>
        )}
        <div className="min-w-0">
          <div className={`text-[11px] font-black tracking-wide ${file ? 'text-emerald-700' : 'text-[#004E86]'}`}>
            {file ? '✓ PHOTO ADDED — tap to replace' : title}
          </div>
          <div className="text-[10px] text-slate-500 leading-snug">{hint}</div>
        </div>
      </div>
    </button>
  );

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <Slot slot="cert" file={certFile} inputRef={certRef} title="CERTIFICATION LABEL" hint="VIN, GVWR & axle ratings — driver door jamb" />
        <Slot slot="tire" file={tireFile} inputRef={tireRef} title="TIRE & LOADING LABEL" hint="Occupant + cargo capacity — same door jamb" />
      </div>
      {(certFile || tireFile) && (
        <button
          type="button"
          onClick={analyze}
          disabled={busy}
          className="mt-2.5 w-full h-12 rounded-xl bg-[#004E86] hover:bg-[#003a65] disabled:opacity-60 text-white text-sm font-black tracking-[0.06em] transition-colors"
          data-testid={`rigready-${idPrefix}-read-labels-btn`}
        >
          {busy ? 'READING YOUR LABELS\u2026' : `READ MY LABEL${certFile && tireFile ? 'S (2)' : ''} \u2192`}
        </button>
      )}
    </div>
  );
};

// Conservative display-only dedupe: "Sierra HD Sierra 2500 HD" → "Sierra 2500 HD",
// "F-150 F-150" → "F-150". Keeps the LAST (most specific) occurrence of a repeated
// word. Raw fields are preserved in state/draft sources — this is display only.
export const normalizeVehicleName = (...parts) => {
  const tokens = parts.filter(Boolean).join(' ').split(/\s+/).filter(Boolean);
  const counts = {};
  tokens.forEach((t) => { const k = t.toLowerCase(); counts[k] = (counts[k] || 0) + 1; });
  const emitted = {};
  const out = [];
  for (const t of tokens) {
    const k = t.toLowerCase();
    emitted[k] = (emitted[k] || 0) + 1;
    if (counts[k] > 1 && emitted[k] < counts[k]) continue;
    out.push(t);
  }
  return out.join(' ');
};

const FUEL_MAP = { gas: 'Gasoline', gasoline: 'Gasoline', diesel: 'Diesel', hybrid: 'Hybrid', electric: 'Electric' };

// Extract a formatted powertrain display ("6.6L V8 Gasoline") from trim/engine text.
export const formatPowertrain = (...sources) => {
  const text = sources.filter(Boolean).join(' ');
  const disp = (text.match(/\b(\d+(?:\.\d+)?)\s*L\b/i) || [])[1] || null;
  const cylM = text.match(/\bV-?(\d+)\b/i) || text.match(/\b(\d+)[-\s]?cyl(?:inder)?s?\b/i);
  const cyl = cylM ? `V${cylM[1]}` : null;
  const fuelM = text.match(/\b(gasoline|gas|diesel|hybrid|electric)\b/i);
  const fuel = fuelM ? FUEL_MAP[fuelM[1].toLowerCase()] : null;
  const display = [disp ? `${disp}L` : null, cyl, fuel].filter(Boolean).join(' ') || null;
  return { display, hasFuel: !!fuel, hasDisp: !!disp, hasCyl: !!cyl };
};

// Remove powertrain descriptors from the vehicle NAME only when they are
// confidently represented in the separate engine/powertrain display line.
export const stripPowertrainFromName = (name, pt) => {
  if (!pt || !pt.display) return name;
  return name.split(/\s+/).filter((t) => {
    const k = t.toLowerCase();
    if (pt.hasFuel && FUEL_MAP[k]) return false;
    if (pt.hasDisp && /^\d+(\.\d+)?l$/.test(k)) return false;
    if (pt.hasCyl && (/^v-?\d+$/.test(k) || /^\d+-?cyl(inder)?s?$/.test(k))) return false;
    return true;
  }).join(' ');
};

const ATTRIB_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

// Hero headline is locked for ad message-matching. (Variant support removed
// pre-launch per launch spec — reintroduce only with real conversion data.)
const HEADLINE = 'WHAT RV CAN YOUR VEHICLE ACTUALLY TOW?';

const YEARS = Array.from({ length: 33 }, (_, i) => String(2027 - i));
const MAKES = [
  'Chevrolet', 'Ford', 'GMC', 'Ram', 'Toyota', 'Nissan', 'Jeep', 'Honda', 'Hyundai', 'Kia',
  'Subaru', 'Dodge', 'Cadillac', 'Buick', 'Lincoln', 'Lexus', 'Acura', 'Infiniti', 'Mazda',
  'Volkswagen', 'Audi', 'BMW', 'Mercedes-Benz', 'Volvo', 'Land Rover', 'Rivian', 'Tesla', 'Other',
];

const pushEvent = (event, params = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
};

// Anonymous funnel session id — analytics only, no PII.
const getAnonId = () => {
  try {
    let id = localStorage.getItem('rigready.anon_id');
    if (!id) {
      id = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem('rigready.anon_id', id);
    }
    return id;
  } catch { return null; }
};

// Fire-and-forget server-side funnel persistence (mirrors dataLayer events).
const trackFunnel = (event, extra = {}) => {
  try {
    const anonId = getAnonId();
    if (!anonId) return;
    axios.post(`${API}/rigready/track`, { anon_id: anonId, event, ...extra }).catch(() => {});
  } catch { /* noop */ }
};

const fmt = (n) => (n || n === 0 ? Number(n).toLocaleString() : null);

export default function RigReadyPage() {
  const location = useLocation();
  const [year, setYear] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [trim, setTrim] = useState('');
  const [modelOptions, setModelOptions] = useState([]);
  const [vin, setVin] = useState('');
  const [vinFromLabel, setVinFromLabel] = useState(false);
  const [stickerBusy, setStickerBusy] = useState(false);
  const [showMatchForm, setShowMatchForm] = useState(false);  const matchFormRef = useRef(null);
  const refineInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const startedRef = useRef(false);
  const viewedRef = useRef(false);
  const resultRef = useRef(null);
  const toolRef = useRef(null);

  const params = new URLSearchParams(location.search);
  const headline = HEADLINE;

  useEffect(() => {
    document.title = 'What RV Can My Vehicle Tow? | Rig Ready TowIQ';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = 'Check your vehicle\u2019s towing limits and find RVs that match your truck or SUV with Rig Ready TowIQ by RV Match A.I.';
    // Preserve ad attribution + advisor link for the funnel handoff
    try {
      const found = {};
      ATTRIB_KEYS.forEach((k) => { const v = params.get(k); if (v) found[k] = v; });
      if (document.referrer && !window.location.href.startsWith(document.referrer.split('/').slice(0, 3).join('/'))) {
        found.referrer = document.referrer;
      }
      if (Object.keys(found).length) {
        const prev = JSON.parse(sessionStorage.getItem('rigready.attrib') || '{}');
        sessionStorage.setItem('rigready.attrib', JSON.stringify({ ...found, ...prev }));
      }
      const adv = params.get('advisor');
      if (adv) sessionStorage.setItem('rigready.advisor', adv);
    } catch { /* noop */ }
    if (!viewedRef.current) {
      viewedRef.current = true;
      pushEvent('rigready_page_view');
      let attrib = {};
      try { attrib = JSON.parse(sessionStorage.getItem('rigready.attrib') || '{}'); } catch { /* noop */ }
      trackFunnel('rigready_page_view', {
        attribution: Object.keys(attrib).length ? attrib : null,
        referrer: document.referrer || null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markStart = useCallback(() => {
    if (!startedRef.current) {
      startedRef.current = true;
      pushEvent('towiq_start');
      trackFunnel('towiq_start');
    }
  }, []);

  // Prepared SECONDARY Google Ads conversion for towiq_result — fires only
  // once per page and only when a real label is supplied (never the primary
  // lead label). Empty env = no-op.
  const towiqConvRef = useRef(false);
  const fireTowiqConversion = () => {
    try {
      const label = process.env.REACT_APP_GADS_TOWIQ_LABEL;
      if (!label || towiqConvRef.current || typeof window.gtag !== 'function') return;
      towiqConvRef.current = true;
      window.gtag('event', 'conversion', { send_to: `AW-18458878897/${label}`, value: 1.0, currency: 'USD' });
    } catch { /* noop */ }
  };

  // Progressive model selection from NHTSA vPIC (the same structured source
  // TowIQ decodes VINs against). Falls back silently to free-text if the
  // public API is slow/unavailable — accuracy over forced dropdowns.
  useEffect(() => {
    setModelOptions([]);
    if (!year || !make || make === 'Other') return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(
          `https://vpic.nhtsa.dot.gov/api/vehicles/GetModelsForMakeYear/make/${encodeURIComponent(make)}/modelyear/${year}?format=json`,
          { signal: AbortSignal.timeout ? AbortSignal.timeout(6000) : undefined },
        );
        const j = await r.json();
        const names = [...new Set((j?.Results || []).map((x) => x.Model_Name).filter(Boolean))].sort();
        if (alive && names.length) setModelOptions(names);
      } catch { /* keep free-text input */ }
    })();
    return () => { alive = false; };
  }, [year, make]);

  const checkVehicle = async () => {
    const vinClean = vin.trim().toUpperCase();
    const useVin = vinClean.length === 17;
    if (!useVin && (!year || !make || !model.trim())) {
      setError(vinClean ? 'A VIN must be exactly 17 characters — or use Year/Make/Model instead.' : 'Select your year and make, then your model — or enter your 17-character VIN.');
      return;
    }
    setError('');
    setLoading(true);
    setResult(null);
    try {
      const modelFull = trim.trim() ? `${model.trim()} ${trim.trim()}` : model.trim();
      const payload = useVin ? { vin: vinClean } : { year, make, model: modelFull };
      const r = await axios.post(`${API}/vehicle/lookup`, payload);
      if (!r.data?.success || (!r.data.tow_capacity_lbs && !r.data.tow_capacity_range)) {
        setError("We couldn't confidently determine towing specs for that configuration. Double-check the model spelling, or add the engine (e.g. \u201c3.0L Duramax\u201d) and try again.");
      } else {
        if (useVin) {
          if (r.data.year) setYear(String(r.data.year));
          if (r.data.make) setMake(r.data.make);
          if (r.data.model) setModel(r.data.model);
        }
        setResult(r.data);
        persistDraft(r.data);
        setShowMatchForm(true); // form auto-appears below the result
        pushEvent('vehicle_identified', { vehicle_year: r.data.year || year, vehicle_make: r.data.make || make, method: useVin ? 'vin' : 'ymm' });
        pushEvent('towiq_result', { confidence: r.data.confidence || 'unknown' });
        trackFunnel('vehicle_identified', {
          vehicle: { year: String(r.data.year || year), make: r.data.make || make, model: r.data.model || modelFull, trim: trim.trim() || null },
          vin_used: useVin,
        });
        trackFunnel('towiq_result', {
          result: {
            tow_capacity_lbs: r.data.tow_capacity_lbs || null,
            tow_capacity_range: r.data.tow_capacity_range || null,
            confidence: r.data.confidence || 'unknown',
            source_state: r.data.sticker_verified ? 'label_verified' : (r.data.confidence === 'high' ? 'configuration' : 'estimated'),
          },
        });
        fireTowiqConversion();
        setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      }
    } catch {
      setError('The lookup service hit a snag. Give it another try in a moment.');
    }
    setLoading(false);
  };

  // Door-jamb label photos → OCR → exact GVWR/VIN. Accepts BOTH labels at
  // once (Certification + Tire & Loading). `refine` merges into an existing
  // result. Returns true on success so uploaders can clear their slots.
  const resizeImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1800;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const analyzeStickers = async (files, refine = false) => {
    const imgs = (Array.isArray(files) ? files : [files]).filter((f) => f && f.type?.startsWith('image/'));
    if (!imgs.length) return false;
    markStart();
    pushEvent('door_label_scan_started', { photos: imgs.length });
    setError('');
    setStickerBusy(true);
    let ok = false;
    try {
      const images = await Promise.all(imgs.map(resizeImage));
      const r = await axios.post(`${API}/vehicle/lookup-sticker`, { images_base64: images });
      const d = r.data || {};
      const vinRead = d.vin && String(d.vin).trim().length === 17 ? String(d.vin).trim().toUpperCase() : null;
      if (vinRead) {
        setVin(vinRead);
        setVinFromLabel(true);
      }
      const lvNew = d.label_verified || (d.gvwr_lbs ? { gvwr_lbs: d.gvwr_lbs } : {});
      if (Object.keys(lvNew).length) {
        if (lvNew.occupant_cargo_lbs) pushEvent('tire_loading_label_uploaded');
        if (lvNew.gvwr_lbs || lvNew.gawr_front_lbs) pushEvent('certification_label_uploaded');
        pushEvent('vehicle_specs_verified');
      }
      if (refine && result) {
        const merged = {
          ...result,
          vin: vinRead || result.vin,
          gvwr_lbs: lvNew.gvwr_lbs ?? d.gvwr_lbs ?? result.gvwr_lbs,
          sticker_verified: true,
          label_verified: { ...(result.label_verified || {}), ...lvNew },
        };
        setResult(merged);
        persistDraft(merged);
        ok = true;
      } else if (d.success) {
        if (d.year) setYear(String(d.year));
        if (d.make) setMake(d.make);
        if (d.model) setModel(d.model);
        setResult({ ...d, sticker_verified: true });
        persistDraft(d);
        setShowMatchForm(true);
        ok = true;
        if (d.tow_capacity_lbs || d.tow_capacity_range) {
          pushEvent('vehicle_identified', { method: 'sticker' });
          pushEvent('towiq_result', { confidence: d.confidence || 'unknown' });
          trackFunnel('vehicle_identified', {
            vehicle: { year: String(d.year || year), make: d.make || make, model: d.model || model, trim: trim.trim() || null },
            vin_used: Boolean(vinRead),
          });
          trackFunnel('towiq_result', {
            result: {
              tow_capacity_lbs: d.tow_capacity_lbs || null,
              tow_capacity_range: d.tow_capacity_range || null,
              confidence: d.confidence || 'unknown',
              source_state: 'label_verified',
            },
          });
          fireTowiqConversion();
        }
        setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      } else {
        setError("We couldn't read enough from those photos — try sharper, well-lit shots of your door-jamb labels.");
      }
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Couldn't read those photos — try sharper, well-lit shots of your door-jamb labels.");
    }
    setStickerBusy(false);
    return ok;
  };

  // Write the vehicle + TowIQ numbers into the RV Match draft so the form
  // (which restores this key on mount) never asks for them again.
  const persistDraft = (res) => {
    try {
      const KEY = 'rvmatch_form_draft_v2';
      const existing = JSON.parse(localStorage.getItem(KEY) || '{}');
      const lv = res?.label_verified || {};
      const payloadVal = lv.occupant_cargo_lbs || res?.payload_lbs || '';
      localStorage.setItem(KEY, JSON.stringify({
        ...existing,
        hasTowVehicle: true,
        towVehicle: {
          year, make,
          model: stripPowertrainFromName(
            normalizeVehicleName(model.trim(), trim.trim()),
            formatPowertrain(trim, res?.engine)
          ),
          engine: formatPowertrain(trim, res?.engine).display || res?.engine || '',
          capacity: res?.tow_capacity_lbs ? String(res.tow_capacity_lbs) : '',
          capacitySource: res?.tow_capacity_lbs ? (res?.confidence === 'high' ? 'configuration' : 'estimated') : '',
          payload: payloadVal ? String(payloadVal) : '',
          payloadSource: lv.occupant_cargo_lbs ? 'verified' : (res?.payload_lbs ? 'estimated' : ''),
          gvwr: lv.gvwr_lbs ? String(lv.gvwr_lbs) : (res?.gvwr_lbs ? String(res.gvwr_lbs) : ''),
          gvwrSource: lv.gvwr_lbs ? 'verified' : (res?.gvwr_lbs ? 'estimated' : ''),
          vin: (res?.vin && String(res.vin).trim().length === 17)
            ? String(res.vin).trim().toUpperCase()
            : (vin.trim().length === 17 ? vin.trim().toUpperCase() : ''),
        },
      }));
    } catch { /* private mode — form will simply ask */ }
  };

  const startRvMatch = () => {
    pushEvent('rvmatch_start');
    trackFunnel('rvmatch_start');
    persistDraft(result);
    setShowMatchForm(true);
    setTimeout(() => matchFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
  };

  const scrollToTool = () => toolRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const lv = result?.label_verified || {};
  const verifiedPayload = lv.occupant_cargo_lbs || null;
  let shopHigh = result?.tow_capacity_lbs ? result.tow_capacity_lbs * 0.8 : null;
  let payloadLimited = false;
  if (shopHigh && verifiedPayload) {
    // Travel-trailer tongue weight ≈ 12% of trailer weight; reserve ~25% of
    // the verified occupant+cargo capacity for passengers and gear.
    const payloadCap = (verifiedPayload * 0.75) / 0.12;
    if (payloadCap < shopHigh) { shopHigh = payloadCap; payloadLimited = true; }
  }
  let shopLow = shopHigh ? Math.min(result.tow_capacity_lbs * 0.6, shopHigh * 0.75) : null;
  shopHigh = shopHigh ? Math.round(shopHigh / 100) * 100 : null;
  shopLow = shopLow ? Math.round(shopLow / 100) * 100 : null;

  const selectCls = 'w-full h-12 rounded-lg border border-slate-300 bg-white px-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#004E86]';
  const labelCls = 'block text-[11px] font-bold tracking-[0.14em] text-slate-500 mb-1.5';

  return (
    <div className="min-h-screen bg-[#F7F9FB] text-slate-900" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Minimal header — no funnel-leaking navigation */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-black tracking-[0.18em] text-[#004E86]">RV MATCH A.I.</div>
            <div className="text-[11px] font-semibold tracking-[0.22em] text-slate-500 mt-0.5">RIG READY &middot; TowIQ</div>
          </div>
          <a href="#how-it-works" className="text-xs font-semibold text-slate-500 hover:text-[#004E86]" data-testid="rigready-how-link">How It Works</a>
        </div>
      </header>

      {/* Hero + tool, above the fold */}
      <section className="max-w-3xl mx-auto px-4 pt-10 pb-8 md:pt-16">
        <h1 className="text-3xl md:text-5xl font-black leading-tight tracking-tight text-slate-900" data-testid="rigready-headline">
          {headline}
        </h1>
        <p className="mt-3 text-base md:text-lg text-slate-600 max-w-xl">
          Find your vehicle&rsquo;s towing limits and discover RVs that actually match what you can tow.
        </p>

        <div ref={toolRef} className="mt-8 rounded-2xl bg-white border border-slate-200 shadow-sm p-5 md:p-7" data-testid="rigready-tool">
          <div className="text-[11px] font-black tracking-[0.22em] text-[#004E86] mb-4">START WITH YOUR VEHICLE</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>YEAR</label>
              <select value={year} onChange={(e) => { markStart(); setYear(e.target.value); }} className={selectCls} data-testid="rigready-year">
                <option value="">Select Year</option>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>MAKE</label>
              <select value={make} onChange={(e) => { markStart(); setMake(e.target.value); }} className={selectCls} data-testid="rigready-make">
                <option value="">Select Make</option>
                {MAKES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>MODEL</label>
              {modelOptions.length > 0 ? (
                <select value={model} onChange={(e) => { markStart(); setModel(e.target.value); }} className={selectCls} data-testid="rigready-model-select">
                  <option value="">Select Model</option>
                  {modelOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              ) : (
                <input value={model} onChange={(e) => { markStart(); setModel(e.target.value); }} placeholder="e.g. Sierra 1500, F-150, Grand Cherokee" className={selectCls} data-testid="rigready-model" />
              )}
            </div>
            <div>
              <label className={labelCls}>ENGINE / TRIM</label>
              <input value={trim} onChange={(e) => setTrim(e.target.value)} placeholder="e.g. Denali 3.0L Duramax" className={selectCls} data-testid="rigready-trim" />
              <p className="mt-1 text-[11px] text-slate-500">Recommended for a more accurate result</p>
            </div>
          </div>

          {/* VIN + door labels — exact-result paths */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className={labelCls}>SNAP YOUR DOOR-JAMB LABELS <span className="font-normal normal-case tracking-normal">(fastest — we read your VIN &amp; exact limits for you)</span></label>
                <DualLabelUploader onAnalyze={(files) => analyzeStickers(files, false)} busy={stickerBusy} idPrefix="main" />
              </div>
              <div>
                <label className={labelCls}>OR TYPE YOUR VIN <span className="font-normal normal-case tracking-normal">(skips the fields above)</span></label>
                <input
                  value={vin}
                  onChange={(e) => { markStart(); setVin(e.target.value.toUpperCase()); setVinFromLabel(false); }}
                  placeholder="17-character VIN (door jamb or windshield)"
                  maxLength={17}
                  className={`${selectCls} font-mono tracking-wide ${vinFromLabel ? 'border-emerald-400 bg-emerald-50/40' : ''}`}
                  data-testid="rigready-vin"
                />
                {vinFromLabel && (
                  <p className="mt-1 text-[11px] font-bold text-emerald-600" data-testid="rigready-vin-autofilled">
                    ✓ VIN read from your door label and filled in automatically
                  </p>
                )}
              </div>
            </div>
          </div>
          {error && <p className="mt-4 text-sm font-medium text-rose-600" data-testid="rigready-error">{error}</p>}
          <button
            onClick={checkVehicle}
            disabled={loading}
            className="mt-5 w-full h-14 rounded-xl bg-[#004E86] hover:bg-[#003a65] disabled:opacity-60 text-white text-base font-black tracking-[0.08em] transition-colors"
            data-testid="rigready-check-btn"
          >
            {loading ? 'ANALYZING YOUR VEHICLE\u2026' : 'CHECK MY TOW CAPACITY'}
          </button>
          <p className="mt-2.5 text-center text-xs text-slate-500">Free &middot; No signup required</p>
        </div>
      </section>

      {/* Result */}
      {result && (
        <section ref={resultRef} className="max-w-3xl mx-auto px-4 pb-4" data-testid="rigready-result">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 md:p-7">
            <div className="text-[11px] font-black tracking-[0.22em] text-emerald-600">YOUR RIG IS READY</div>
            <h2 className="mt-1 text-2xl md:text-3xl font-black text-slate-900">
              {(year || make || model)
                ? stripPowertrainFromName(
                    normalizeVehicleName(year, make, model, trim),
                    formatPowertrain(trim, result.engine)
                  )
                : 'Your vehicle — read from your door sticker'}
            </h2>
            {(formatPowertrain(trim, result.engine).display || result.engine) && (
              <p className="text-sm text-slate-500 mt-0.5" data-testid="rigready-engine-line">
                {formatPowertrain(trim, result.engine).display || result.engine}
              </p>
            )}

            <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
              {fmt(result.tow_capacity_lbs) && (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="rigready-tow-rating">
                  <div className="text-[10px] font-bold tracking-[0.14em] text-slate-500">MAXIMUM TOW RATING</div>
                  <div className="text-xl md:text-2xl font-black text-[#004E86] mt-1">{fmt(result.tow_capacity_lbs)} lbs</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {result.confidence === 'high' ? 'configuration-based — verify against manufacturer ratings' : 'estimate — varies by exact configuration'}
                  </div>
                </div>
              )}
              {verifiedPayload ? (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="rigready-payload-verified">
                  <div className="text-[10px] font-bold tracking-[0.14em] text-emerald-700">OCCUPANT + CARGO CAPACITY</div>
                  <div className="text-xl md:text-2xl font-black text-emerald-800 mt-1">{fmt(verifiedPayload)} lbs</div>
                  <div className="text-[10px] font-bold text-emerald-700 mt-0.5">FROM YOUR VEHICLE LABEL</div>
                </div>
              ) : fmt(result.payload_lbs) ? (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-[10px] font-bold tracking-[0.14em] text-slate-500">PAYLOAD CAPACITY</div>
                  <div className="text-xl md:text-2xl font-black text-slate-900 mt-1">{fmt(result.payload_lbs)} lbs</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">estimate — verify with your Tire and Loading label</div>
                </div>
              ) : null}
              {result.gvwr_lbs && (lv.gvwr_lbs || result.sticker_verified) ? (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                  <div className="text-[10px] font-bold tracking-[0.14em] text-emerald-700">GVWR</div>
                  <div className="text-xl md:text-2xl font-black text-emerald-800 mt-1">{fmt(result.gvwr_lbs)} lbs</div>
                  <div className="text-[10px] font-bold text-emerald-700 mt-0.5">FROM YOUR VEHICLE LABEL</div>
                </div>
              ) : fmt(result.gvwr_lbs) ? (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-[10px] font-bold tracking-[0.14em] text-slate-500">GVWR</div>
                  <div className="text-xl md:text-2xl font-black text-slate-900 mt-1">{fmt(result.gvwr_lbs)} lbs</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">configuration data</div>
                </div>
              ) : null}
              {fmt(lv.gawr_front_lbs) && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                  <div className="text-[10px] font-bold tracking-[0.14em] text-emerald-700">FRONT GAWR</div>
                  <div className="text-xl md:text-2xl font-black text-emerald-800 mt-1">{fmt(lv.gawr_front_lbs)} lbs</div>
                  <div className="text-[10px] font-bold text-emerald-700 mt-0.5">FROM YOUR VEHICLE LABEL</div>
                </div>
              )}
              {fmt(lv.gawr_rear_lbs) && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                  <div className="text-[10px] font-bold tracking-[0.14em] text-emerald-700">REAR GAWR</div>
                  <div className="text-xl md:text-2xl font-black text-emerald-800 mt-1">{fmt(lv.gawr_rear_lbs)} lbs</div>
                  <div className="text-[10px] font-bold text-emerald-700 mt-0.5">FROM YOUR VEHICLE LABEL</div>
                </div>
              )}
              {fmt(result.gcwr_lbs) && (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-[10px] font-bold tracking-[0.14em] text-slate-500">GCWR</div>
                  <div className="text-xl md:text-2xl font-black text-slate-900 mt-1">{fmt(result.gcwr_lbs)} lbs</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">configuration estimate</div>
                </div>
              )}
              {result.tow_capacity_range && (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-[10px] font-bold tracking-[0.14em] text-slate-500">RANGE FOR THIS MODEL</div>
                  <div className="text-base md:text-lg font-black text-slate-900 mt-1">{result.tow_capacity_range}</div>
                </div>
              )}
              {shopLow && shopHigh && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 col-span-2 md:col-span-1" data-testid="rigready-shop-range">
                  <div className="text-[10px] font-bold tracking-[0.14em] text-emerald-700">SUGGESTED RV SHOPPING RANGE</div>
                  <div className="text-base md:text-lg font-black text-emerald-800 mt-1">{fmt(shopLow)} &ndash; {fmt(shopHigh)} lbs</div>
                  <div className="text-[10px] text-emerald-700 mt-0.5 leading-relaxed">
                    {payloadLimited
                      ? 'limited by your verified occupant + cargo capacity, not the tow rating. '
                      : ''}
                    A conservative starting range for shopping — final compatibility depends on your
                    vehicle-specific payload, tongue or pin weight, passengers, cargo and manufacturer limits.
                  </div>
                </div>
              )}
            </div>

            {result.notes && (
              <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                <span className="font-bold">Configuration note:</span> {result.notes}
              </p>
            )}

            {!result.sticker_verified && (
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
                <div className="text-[11px] font-black tracking-[0.14em] text-slate-700">WANT A MORE PRECISE RESULT?</div>
                <p className="mt-1 text-xs text-slate-600 font-medium">
                  Your driver-side door-jamb labels &mdash; the <span className="font-bold">Certification Label</span> (GVWR
                  &amp; axle ratings) and the <span className="font-bold">Tire and Loading label</span> (occupant + cargo
                  capacity) &mdash; let TowIQ use limits from your actual truck instead of configuration estimates.
                  Snap one or both:
                </p>
                <div className="mt-2.5">
                  <DualLabelUploader onAnalyze={(files) => analyzeStickers(files, true)} busy={stickerBusy} idPrefix="refine" />
                </div>
              </div>
            )}
            {result.sticker_verified && !verifiedPayload && (
              <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3.5 flex-wrap">
                <p className="text-xs text-slate-600 font-medium">
                  Have the <span className="font-bold">Tire and Loading label</span> too? It gives your truck&rsquo;s exact
                  occupant + cargo capacity.
                </p>
                <button
                  onClick={() => refineInputRef.current?.click()}
                  disabled={stickerBusy}
                  className="text-xs font-bold px-3.5 py-2 rounded-lg bg-[#004E86] text-white hover:bg-[#003a65] disabled:opacity-60"
                  data-testid="rigready-tireload-btn"
                >
                  {stickerBusy ? 'Reading\u2026' : 'Scan it'}
                </button>
                <input
                  ref={refineInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { analyzeStickers(Array.from(e.target.files || []), true); e.target.value = ''; }}
                  data-testid="rigready-refine-input-2"
                />
              </div>
            )}

            {/* Safety / accuracy */}
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="text-xs font-black tracking-[0.12em] text-amber-800">TOW RATING ISN&rsquo;T THE WHOLE STORY</div>
              <p className="mt-1.5 text-sm text-amber-900 leading-relaxed">
                Two trucks with the same maximum tow rating can have very different real-world RV limits.
                Payload, tongue or pin weight, passengers, cargo, hitch ratings and exact vehicle
                configuration all matter.
              </p>
              <p className="mt-1.5 text-xs text-amber-800 leading-relaxed">
                Rig Ready helps narrow your RV search, but your vehicle&rsquo;s labels and manufacturer
                ratings remain the final authority.
              </p>
              {!trim.trim() && (
                <p className="mt-2 text-xs font-semibold text-amber-800">
                  Tip: add your engine/trim above and re-check for a tighter number.
                </p>
              )}
            </div>

            {/* PRIMARY CONVERSION */}
            <div className="mt-7 rounded-2xl bg-[#004E86] p-6 md:p-8 text-white">
              <h3 className="text-xl md:text-2xl font-black leading-snug">
                YOUR VEHICLE IS READY.<br />NOW LET&rsquo;S FIND THE RIGHT RV.
              </h3>
              <p className="mt-2 text-sm text-blue-100 max-w-md">
                We&rsquo;ve already figured out what your vehicle can handle. Now let RV Match A.I. find RVs
                that fit your vehicle, budget and travel style.
              </p>
              <button
                onClick={startRvMatch}
                className="mt-4 w-full md:w-auto md:px-10 h-14 rounded-xl bg-white text-[#004E86] text-base font-black tracking-[0.06em] hover:bg-blue-50 transition-colors"
                data-testid="rigready-rvmatch-cta"
              >
                FIND RVs THAT MATCH MY VEHICLE
              </button>
              <p className="mt-2 text-[11px] text-blue-200">Your vehicle info carries over &mdash; you won&rsquo;t re-enter it.</p>
            </div>
          </div>
        </section>
      )}

      {/* Inline RV Match form — loaded on demand after the conversion CTA */}
      {showMatchForm && (
        <section ref={matchFormRef} id="rvmatch-inline" className="border-t-4 border-[#004E86] bg-white" data-testid="rigready-inline-form">
          <div className="max-w-3xl mx-auto px-4 pt-8">
            <div className="text-[11px] font-black tracking-[0.22em] text-[#004E86]">RV MATCH A.I.</div>
            <h2 className="mt-1 text-2xl md:text-3xl font-black text-slate-900">Let&rsquo;s find your RV.</h2>
            <p className="mt-1 text-sm text-slate-600">Your vehicle info is already loaded — just answer a few quick questions.</p>
          </div>
          <Suspense fallback={<div className="py-16 text-center text-sm font-semibold text-slate-500">Loading your match form&hellip;</div>}>
            <RvMatchForm
              embedMode={true}
              hideSignInPrompt={true}
              advisorSlug={(() => { try { return params.get('advisor') || sessionStorage.getItem('rigready.advisor') || null; } catch { return params.get('advisor'); } })()}
            />
          </Suspense>
        </section>
      )}

      {/* How it works */}
      <section id="how-it-works" className="max-w-3xl mx-auto px-4 py-12">
        <h2 className="text-xl md:text-2xl font-black tracking-tight">HOW RIG READY WORKS</h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            ['1', 'TELL US WHAT YOU DRIVE', 'Identify your vehicle and towing configuration.'],
            ['2', 'KNOW YOUR REAL LIMITS', 'Understand the towing and payload numbers that matter when shopping for an RV.'],
            ['3', 'FIND YOUR RV MATCH', 'See RVs that fit your vehicle, budget and travel style.'],
          ].map(([n, t, d]) => (
            <div key={n} className="rounded-xl bg-white border border-slate-200 p-5">
              <div className="w-8 h-8 rounded-full bg-[#004E86] text-white flex items-center justify-center font-black text-sm">{n}</div>
              <div className="mt-3 text-sm font-black tracking-[0.08em]">{t}</div>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Positioning */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-4 py-14">
          <h2 className="text-2xl md:text-4xl font-black tracking-tight">STOP SHOPPING BACKWARDS.</h2>
          <p className="mt-3 text-slate-300 max-w-lg leading-relaxed">
            Most people find an RV they love and then ask whether their vehicle can tow it.
          </p>
          <p className="mt-2 text-lg font-bold text-white">Start with your vehicle. End with the right RV.</p>
          <button
            onClick={scrollToTool}
            className="mt-6 px-8 h-13 py-3.5 rounded-xl bg-white text-slate-900 font-black tracking-[0.06em] hover:bg-slate-100 transition-colors"
            data-testid="rigready-check-vehicle-cta"
          >
            CHECK MY VEHICLE
          </button>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-3xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-center">
          {['NO SIGNUP TO CHECK YOUR VEHICLE', 'BUILT FOR REAL RV SHOPPERS', 'MATCHED TO YOUR VEHICLE'].map((t) => (
            <div key={t} className="rounded-xl border border-slate-200 bg-white px-4 py-5 text-xs font-black tracking-[0.1em] text-slate-700">{t}</div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-6 text-[11px] text-slate-500 leading-relaxed">
          Towing figures are estimates for the model you selected and vary by exact configuration (engine, axle ratio,
          packages, cab and bed). This is not a manufacturer-certified safe-towing determination. Always confirm ratings
          for your exact vehicle using its door-jamb labels, owner&rsquo;s manual and manufacturer documentation.
          <div className="mt-2 font-semibold text-slate-400">RV Match A.I. &middot; Rig Ready TowIQ</div>
        </div>
      </footer>

      {/* Sticky mobile CTA — only after a result exists and before the form is open */}
      {result && !showMatchForm && (
        <div className="fixed bottom-0 inset-x-0 md:hidden bg-white/95 backdrop-blur border-t border-slate-200 p-3" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
          <button
            onClick={startRvMatch}
            className="w-full h-12 rounded-xl bg-[#004E86] text-white text-sm font-black tracking-[0.06em]"
            data-testid="rigready-sticky-cta"
          >
            FIND RVs THAT MATCH MY VEHICLE
          </button>
        </div>
      )}
      {result && <div className="h-20 md:hidden" />}
    </div>
  );
}
